export type RealityCapability =
  | "notification"
  | "audio"
  | "haptics"
  | "lighting"
  | "ar-overlay"
  | "file-export"
  | "device-command";

export interface RealityRequest<T = unknown> {
  capability: RealityCapability;
  action: string;
  payload?: T;
  entityId?: string;
}

export interface RealityAuditEntry {
  atMs: number;
  capability: RealityCapability;
  action: string;
  allowed: boolean;
  entityId?: string;
  reason: string;
}

export type RealityHandler = (request: RealityRequest) => unknown | Promise<unknown>;

export class RealityBridge {
  private readonly grants = new Set<RealityCapability>();
  private readonly handlers = new Map<RealityCapability, RealityHandler>();
  private readonly auditLog: RealityAuditEntry[] = [];

  registerHandler(capability: RealityCapability, handler: RealityHandler): () => void {
    this.handlers.set(capability, handler);
    return () => this.handlers.delete(capability);
  }

  grant(capability: RealityCapability): void { this.grants.add(capability); }
  revoke(capability: RealityCapability): void { this.grants.delete(capability); }
  isGranted(capability: RealityCapability): boolean { return this.grants.has(capability); }

  async request<T = unknown>(request: RealityRequest): Promise<T> {
    const handler = this.handlers.get(request.capability);
    if (!this.grants.has(request.capability)) {
      this.audit(request, false, "capability-not-granted");
      throw new Error(`RealityBridge denied capability: ${request.capability}`);
    }
    if (!handler) {
      this.audit(request, false, "no-host-handler");
      throw new Error(`RealityBridge has no host handler for: ${request.capability}`);
    }
    this.audit(request, true, "granted");
    return await handler(request) as T;
  }

  auditEntries(): RealityAuditEntry[] {
    return this.auditLog.map(entry => ({ ...entry }));
  }

  private audit(request: RealityRequest, allowed: boolean, reason: string): void {
    this.auditLog.push({
      atMs: Date.now(),
      capability: request.capability,
      action: request.action,
      allowed,
      entityId: request.entityId,
      reason,
    });
    if (this.auditLog.length > 512) this.auditLog.splice(0, this.auditLog.length - 512);
  }
}
